import * as d3 from "d3"
import { useEffect, useRef } from "react"
import { colors } from "../../../utils/styles"
import { formatDate } from "../../../utils/date"

export const BrazilianCreditBondsPlot = ({ data, width = 1875, height = 700, setSVG = () => {}, legend = () => {}, comparativeCurves = [] }) => {
  const ref = useRef(null)

  useEffect(() => {
    if (!data || !data.length) return

    const svg = d3.select(ref.current).attr("width", width).attr("height", height)

    svg.selectAll("*").remove()

    const margin = { top: 20, right: 40, bottom: 40, left: 40 }
    const chartWidth = width - margin.left - margin.right
    const chartHeight = height - margin.top - margin.bottom

    const tooltip = d3
      .select("body")
      .append("div")
      .style("position", "absolute")
      .style("background", "rgba(0, 0, 0, 0.7)")
      .style("color", "white")
      .style("padding", "8px")
      .style("border-radius", "4px")
      .style("pointer-events", "none")
      .style("font-size", "12px")
      .style("white-space", "nowrap")
      .style("visibility", "hidden")
      .style("font-family", "'Segoe UI'")

    const chart = svg.append("g").attr("transform", `translate(${margin.left}, ${margin.top})`)

    svg
      .append("defs")
      .append("clipPath")
      .attr("id", "clip")
      .append("rect")
      .attr("x", 0)
      .attr("y", 0)
      .attr("width", chartWidth)
      .attr("height", chartHeight)

    svg
      .append("text")
      .attr("x", width / 2)
      .attr("y", height - 10)
      .attr("text-anchor", "middle")
      .style("font-size", "14px")
      .style("font-family", "'Segoe UI'")
      .text("Duration")

    svg
      .append("text")
      .attr("transform", "rotate(-90)")
      .attr("x", -height / 2)
      .attr("y", 10)
      .attr("text-anchor", "middle")
      .style("font-size", "14px")
      .style("font-family", "'Segoe UI'")
      .text("Spread")

    const allCurvesData = comparativeCurves.flatMap((curveObj) => {
      const curveName = Object.keys(curveObj)[0]
      return curveObj[curveName]
    })
    const combinedData = [...data, ...allCurvesData]

    const xExtent = d3.extent(combinedData, (d) => d.duration)
    const yExtent = d3.extent(combinedData, (d) => d.rate)

    const xScale = d3
      .scaleLinear()
      .domain([Math.floor(xExtent[0]) - 0.25, Math.ceil(xExtent[1]) + 0.25])
      .range([0, chartWidth])

    const yScale = d3
      .scaleLinear()
      .domain([Math.floor(yExtent[0]) - 5, Math.ceil(yExtent[1]) + 5])
      .range([chartHeight, 0])

    const xAxis = d3.axisBottom(xScale)
    const yAxis = d3.axisLeft(yScale)

    const xAxisGroup = chart.append("g").attr("transform", `translate(0, ${chartHeight})`).call(xAxis)

    const yAxisGroup = chart.append("g").call(yAxis)

    const xGrid = d3.axisBottom(xScale).tickSize(-chartHeight).tickFormat("")

    const yGrid = d3.axisLeft(yScale).tickSize(-chartWidth).tickFormat("")

    chart.append("g").attr("class", "grid").attr("transform", `translate(0, ${chartHeight})`).call(xGrid).selectAll("line").style("stroke", "#e0e0e0")

    chart.append("g").attr("class", "grid").call(yGrid).selectAll("line").style("stroke", "#e0e0e0")

    const points = chart
      .append("g")
      .attr("clip-path", "url(#clip)")
      .selectAll("circle")
      .data(data)
      .enter()
      .append("circle")
      .attr("cx", (d) => xScale(d.duration))
      .attr("cy", (d) => yScale(d.rate))
      .attr("r", 5)
      .style("fill", colors.primary)
      .on("mouseover", (_, d) => {
        tooltip.style("visibility", "visible").html(
          `<strong>Nome:</strong> ${d.name || ""}<br/>
             <strong>Remuneração:</strong> ${legend(d.rate)}<br/>
             <strong>Duration:</strong> ${d.duration.toFixed(2)} anos<br/>
             <strong>Vencimento:</strong> ${formatDate(d.maturity) || ""}<br/><br/>
             <p>(*) O papel tem cláusula de Resgate ou Amortização Antecipado.</p>
             <p>(**) A cláusula já está no período de exercício.</p>
             <p>(#) Condição atual de negociação em "combo" dessas emissões.</p>
             `,
        )
      })
      .on("mousemove", (event) => {
        tooltip.style("top", event.pageY + 10 + "px").style("left", event.pageX + 10 + "px")
      })
      .on("mouseout", () => {
        tooltip.style("visibility", "hidden")
      })

    chart
      .append("g")
      .attr("clip-path", "url(#clip)")
      .selectAll("text.label")
      .data(data)
      .enter()
      .append("text")
      .attr("x", (d) => xScale(d.duration))
      .attr("y", (d) => yScale(d.rate) + 17)
      .attr("class", "label")
      .style("font-size", "10px")
      .style("font-family", "'Segoe UI'")
      .attr("text-anchor", "middle")
      .text((d) => d.ticker)

    comparativeCurves.forEach((curveObj, idx) => {
      const curveName = Object.keys(curveObj)[0]
      const curveData = curveObj[curveName]

      const lineGenerator = d3
        .line()
        .x((d) => xScale(d.duration))
        .y((d) => yScale(d.rate))
        .curve(d3.curveMonotoneX)

      chart
        .append("path")
        .datum(curveData)
        .attr("class", "comparative-curve")
        .attr("fill", "none")
        .attr("stroke", "black")
        .attr("stroke-width", 2)
        .attr("d", lineGenerator)
    })

    if (comparativeCurves.length > 0) {
      const legendPadding = 10
      const lineHeight = 20
      const titlePadding = 5
      const legendWidth = 200
      const legendHeight = comparativeCurves.length * lineHeight + 30 + titlePadding

      const legend = chart.append("g").attr("class", "legend").attr("transform", `translate(20, 35)`)

      legend
        .append("rect")
        .attr("x", -legendPadding)
        .attr("y", -30)
        .attr("width", legendWidth)
        .attr("height", legendHeight)
        .attr("fill", "white")
        .attr("stroke", "black")
        .attr("stroke-width", 1)

      legend
        .append("text")
        .attr("x", 0)
        .attr("y", -10)
        .style("font-weight", "bold")
        .style("font-family", "'Segoe UI'")
        .style("font-size", "12px")
        .text("Reference")

      comparativeCurves.forEach((curveObj, idx) => {
        const curveName = Object.keys(curveObj)[0]

        legend
          .append("line")
          .attr("x1", 0)
          .attr("y1", idx * 20 + titlePadding)
          .attr("x2", 20)
          .attr("y2", idx * 20 + titlePadding)
          .attr("stroke", "black")
          .attr("stroke-width", 2)

        legend
          .append("text")
          .attr("x", 30)
          .attr("y", idx * 20 + 5 + titlePadding)
          .style("font-family", "'Segoe UI'")
          .style("font-size", "12px")
          .text(curveName)
      })
    }

    const zoom = d3
      .zoom()
      .scaleExtent([1, 10])
      .on("zoom", (event) => {
        const newXScale = event.transform.rescaleX(xScale)
        const newYScale = event.transform.rescaleY(yScale)

        xAxisGroup.call(xAxis.scale(newXScale))
        yAxisGroup.call(yAxis.scale(newYScale))

        points.attr("cx", (d) => newXScale(d.duration)).attr("cy", (d) => newYScale(d.rate))

        chart
          .selectAll("text.label")
          .attr("x", (d) => newXScale(d.duration))
          .attr("y", (d) => newYScale(d.rate) + 17)

        const newLineGenerator = d3
          .line()
          .x((d) => newXScale(d.duration))
          .y((d) => newYScale(d.rate))
          .curve(d3.curveMonotoneX)

        chart.selectAll(".comparative-curve").attr("d", (d) => newLineGenerator(d))
      })
      .on("start", () => svg.style("cursor", "grabbing"))
      .on("end", () => svg.style("cursor", "default"))

    chart.call(zoom).style("cursor", "grab")
  }, [data, comparativeCurves])

  useEffect(() => {
    if (setSVG) setSVG(ref.current)
  }, [setSVG, ref])

  return <svg ref={ref}></svg>
}
